// runtime.ts — runtime application configuration (P3 Tier 2: build-once).
//
// The four REQUIRED values below are the environment-specific inputs the SPA
// cannot boot without. They
// were previously baked into the bundle at compile time via process.env.VUE_APP_*,
// which forced a separate stage/prod build. They now load at *runtime* from
// `window.__APP_CONFIG__`, populated by the env-specific `config.js` that the
// deploy pipeline writes next to the bundle (deploy/ci/lib/deploy.sh →
// write_runtime_config). One bundle therefore runs unchanged in every environment.
//
// Dev fallback: `public/config.js` ships an empty placeholder, so under `pnpm
// serve` each field falls through to its compile-time `.env.development` value —
// local development is unchanged.
//
// `backendApiClientId` is the one OPTIONAL field, and deliberately so. It is
// read only by `core/services/graphAssertion.ts`, which needs it to name the
// backend's own API scope (`api://<id>/access_as_user`) when acquiring the
// assertion the Review Requests SharePoint explorer sends. `write_runtime_config`
// (deploy/ci/lib/common.sh) emits it only when that environment's `.env.*.vue`
// secret carries `VUE_APP_BACKEND_API_CLIENT_ID`, and OMITS the key otherwise
// rather than emitting it empty — an environment that has not enabled the
// explorer must reach the absent branch below, not a configured-but-wrong app id.
// Absent, `acquireAssertion()` answers with the `unconfigured` failure rather
// than a token, the explorer fails closed, and the composer renders its
// "not configured" state — distinct from the "sign in again" one, which now
// belongs only to a session failure. The backend half
// (`SRB_GRAPH_DELEGATED_ENABLED`) gates the same feature server-side and answers
// 503 without it; both halves are required. See deploy/secrets/README.md.

import { log } from "@/logging";

interface AppConfig {
  apiUrl?: string;
  clientId?: string;
  authority?: string;
  redirectUri?: string;
  /** Optional — see the module doc. An app id, not a secret. */
  backendApiClientId?: string;
}

declare global {
  interface Window {
    __APP_CONFIG__?: AppConfig;
  }
}

const injected: AppConfig =
  (typeof window !== "undefined" && window.__APP_CONFIG__) || {};

// Resolve runtime → compile-time fallback, failing fast on a missing value. A
// misconfigured deploy must never silently boot with an undefined API or auth
// endpoint (mirrors the original main.ts guard, now keyed on the resolved value).
function required(
  runtimeValue: string | undefined,
  buildValue: string | undefined,
  name: string,
): string {
  const value = runtimeValue ?? buildValue;
  if (!value) {
    const msg = `Missing runtime config "${name}". Set it in config.js (window.__APP_CONFIG__) or .env.development.`;
    log.error(msg);
    throw new Error(msg);
  }
  return value;
}

export const runtimeConfig = {
  apiUrl: required(injected.apiUrl, process.env.VUE_APP_API_URL, "apiUrl"),
  clientId: required(
    injected.clientId,
    process.env.VUE_APP_CLIENT_ID,
    "clientId",
  ),
  authority: required(
    injected.authority,
    process.env.VUE_APP_AUTHORITY,
    "authority",
  ),
  redirectUri: required(
    injected.redirectUri,
    process.env.VUE_APP_REDIRECT_URI,
    "redirectUri",
  ),
  // Deliberately NOT through `required()` — an absent value is a supported
  // state, not a misconfiguration. See the module doc.
  backendApiClientId:
    injected.backendApiClientId ?? process.env.VUE_APP_BACKEND_API_CLIENT_ID,
} as const;
